import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bug, Mic, MicOff, Send, X, Loader2, Check, AlertCircle, Lightbulb, Info } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map route to human-readable page name
const getPageName = (pathname) => {
  const map = {
    "/": "Dashboard", "/lotti": "Lotti", "/ricette": "Ricette",
    "/ingredienti": "Ingredienti", "/menu": "Menu", "/acquisti": "Acquisti",
    "/fornitori": "Fornitori", "/frigoriferi": "Temperature",
    "/programma-pulizie": "Pulizie", "/task-gestione": "Compiti",
    "/personale/presenze": "Presenze", "/personale/turni": "Turni",
    "/personale/ferie": "Ferie", "/ordini-fornitori": "Ordini",
    "/anagrafica/locali": "Locali", "/anagrafica/utenti": "Utenti",
    "/abbonamento": "Abbonamento", "/impostazioni": "Impostazioni",
  };
  for (const [path, name] of Object.entries(map)) {
    if (pathname === path || pathname.startsWith(path + "/")) return name;
  }
  return pathname.replace(/\//g, " ").trim() || "Pagina";
};

const FeedbackButton = () => {
  const { user, localeCorrente } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("idle"); // idle | capturing | preview | recording | sending | done
  const [screenshotB64, setScreenshotB64] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [trascrizione, setTrascrizione] = useState(null);
  const [audioPath, setAudioPath] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [tipo, setTipo] = useState("problema");
  const [testo, setTesto] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const reset = useCallback(() => {
    setOpen(false);
    setStep("idle");
    setScreenshotB64(null);
    setIsRecording(false);
    setAudioBlob(null);
    setTrascrizione(null);
    setAudioPath(null);
    setTranscribing(false);
    setTipo("problema");
    setTesto("");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Capture screenshot
  const captureScreenshot = useCallback(async () => {
    setStep("capturing");
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 0.7,
        logging: false,
        backgroundColor: null,
        ignoreElements: (el) => el.id === "feedback-fab" || el.dataset?.testid === "feedback-fab"
      });
      // Convert to WebP via canvas (quality 0.9)
      const b64 = canvas.toDataURL("image/webp", 0.9);
      setScreenshotB64(b64);
      setStep("preview");
      setOpen(true);
    } catch (e) {
      console.error("Screenshot fallito:", e);
      toast.error("Impossibile catturare lo screenshot");
      setStep("idle");
    }
  }, []);

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast.error("Microfono non disponibile");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Transcribe audio
  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("file", new File([blob], "voice.webm", { type: "audio/webm" }));
      const resp = await axios.post(`${API}/segnalazioni/transcribe-audio`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        timeout: 30000
      });
      setTrascrizione(resp.data.trascrizione);
      setAudioPath(resp.data.audio_path);
    } catch {
      toast.error("Errore nella trascrizione");
      setTrascrizione("[Trascrizione non disponibile]");
    } finally {
      setTranscribing(false);
    }
  };

  // Send segnalazione
  const inviaFeedback = async () => {
    setStep("sending");
    try {
      const deviceInfo = `${navigator.userAgent.includes("Mobile") ? "Mobile" : "Desktop"} | ${navigator.userAgent.split(/[()]/)[1] || ""}`;
      await axios.post(`${API}/segnalazioni`, {
        screenshot_base64: screenshotB64,
        pagina_url: location.pathname,
        pagina_nome: getPageName(location.pathname),
        device_info: deviceInfo,
        trascrizione: trascrizione,
        audio_path: audioPath,
        locale_id: localeCorrente?.id,
        locale_nome: localeCorrente?.nome,
        tipo: tipo,
        testo: testo.trim() || null,
      }, { headers, timeout: 30000 });
      setStep("done");
      toast.success("Segnalazione inviata!");
      setTimeout(reset, 1500);
    } catch {
      toast.error("Errore nell'invio della segnalazione");
      setStep("preview");
    }
  };

  // Handle voice flow after recording stops
  useEffect(() => {
    if (audioBlob && !isRecording && !transcribing && !trascrizione) {
      transcribeAudio(audioBlob);
    }
  }, [audioBlob, isRecording]);

  // Don't show for superadmin
  if (user?.role === "superadmin") return null;

  return createPortal(
    <>
      {/* FAB flottante */}
      <button
        id="feedback-fab"
        data-testid="feedback-fab"
        onClick={captureScreenshot}
        disabled={step === "capturing"}
        className="fixed z-[9999] bottom-20 right-4 md:bottom-6 md:right-6 w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 text-white shadow-lg shadow-red-500/30 flex items-center justify-center transition-all"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        {step === "capturing" ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Bug className="h-5 w-5" />
        )}
      </button>

      {/* Bottom Sheet Dialog */}
      {open && (
        <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) reset(); }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div 
            className="relative w-full sm:max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl p-4 space-y-3 animate-slide-up max-h-[80vh] overflow-y-auto"
            data-testid="feedback-dialog"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-red-500" />
                <h3 className="text-foreground font-bold text-sm">Segnala problema</h3>
              </div>
              <button onClick={reset} className="text-muted-foreground p-1"><X className="h-4 w-4" /></button>
            </div>

            {/* Screenshot preview */}
            {screenshotB64 && (
              <div className="relative rounded-xl overflow-hidden border border-border">
                <img src={screenshotB64} alt="Screenshot" className="w-full h-32 object-cover object-top" />
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">
                  {getPageName(location.pathname)}
                </div>
              </div>
            )}

            {/* Step: Preview - ask to add voice */}
            {step === "preview" && !audioBlob && !trascrizione && (
              <div className="space-y-3">
                {/* Tipo selector */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5">Tipo di segnalazione</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { key: "problema", label: "Problema", Icon: AlertCircle, color: "text-red-500 bg-red-50 border-red-200" },
                      { key: "proposta", label: "Proposta", Icon: Lightbulb, color: "text-yellow-600 bg-yellow-50 border-yellow-200" },
                      { key: "comunicazione", label: "Info", Icon: Info, color: "text-blue-500 bg-blue-50 border-blue-200" },
                    ].map(({ key, label, Icon, color }) => (
                      <button
                        key={key}
                        onClick={() => setTipo(key)}
                        data-testid={`tipo-btn-${key}`}
                        className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-xs font-medium transition-all ${
                          tipo === key ? color + " ring-2 ring-offset-1 ring-current" : "border-border text-muted-foreground bg-background hover:bg-muted"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Testo libero */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5">Descrizione (facoltativa)</p>
                  <textarea
                    value={testo}
                    onChange={e => setTesto(e.target.value)}
                    placeholder="Descrivi brevemente il problema o la proposta..."
                    rows={3}
                    data-testid="segnalazione-testo"
                    className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={startRecording}
                    variant="outline"
                    className="flex-1 h-10 border-border text-foreground text-sm"
                    data-testid="start-voice-btn"
                  >
                    <Mic className="h-4 w-4 mr-1.5 text-red-500" />
                    Aggiungi vocale
                  </Button>
                  <Button
                    onClick={inviaFeedback}
                    className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white text-sm"
                    data-testid="send-no-voice-btn"
                  >
                    <Send className="h-4 w-4 mr-1.5" />
                    Invia
                  </Button>
                </div>
              </div>
            )}

            {/* Step: Recording */}
            {isRecording && (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-3 py-4">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-foreground font-medium text-sm">Registrazione in corso...</span>
                </div>
                <Button
                  onClick={stopRecording}
                  className="w-full h-11 bg-red-500 hover:bg-red-600 text-white"
                  data-testid="stop-voice-btn"
                >
                  <MicOff className="h-4 w-4 mr-2" />
                  Ferma registrazione
                </Button>
              </div>
            )}

            {/* Step: Transcribing */}
            {transcribing && (
              <div className="flex items-center justify-center gap-3 py-4">
                <Loader2 className="h-5 w-5 text-primary animate-spin" />
                <span className="text-muted-foreground text-sm">Trascrivo il vocale...</span>
              </div>
            )}

            {/* Step: Transcription ready - send */}
            {trascrizione && step !== "sending" && step !== "done" && (
              <div className="space-y-2">
                <div className="bg-muted p-2 rounded-lg">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Trascrizione</p>
                  <p className="text-foreground text-sm leading-relaxed">{trascrizione}</p>
                </div>
                <Button
                  onClick={inviaFeedback}
                  className="w-full h-11 bg-red-500 hover:bg-red-600 text-white"
                  data-testid="send-with-voice-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Invia segnalazione
                </Button>
              </div>
            )}

            {/* Step: Sending */}
            {step === "sending" && (
              <div className="flex items-center justify-center gap-3 py-6">
                <Loader2 className="h-5 w-5 text-primary animate-spin" />
                <span className="text-muted-foreground text-sm">Invio in corso...</span>
              </div>
            )}

            {/* Step: Done */}
            {step === "done" && (
              <div className="flex flex-col items-center gap-2 py-6">
                <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                  <Check className="h-5 w-5 text-green-500" />
                </div>
                <span className="text-foreground font-medium text-sm">Segnalazione inviata!</span>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.32,0.72,0,1); }
      `}</style>
    </>,
    document.body
  );
};

export default FeedbackButton;
